import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationInbox from './NotificationInbox';
import FacilitySelector from './FacilitySelector';
import { DeepechoMark, DeepechoWordmark } from './DeepechoLogos';
import { useHelp } from '../help/HelpProvider';

interface MenuItem {
  icon: string;
  title: string;
  link: string;
}

interface MenuProps {
  mainMenuItems: MenuItem[];
  menuOpen: boolean;
  handleLogout: () => void;
  toggleMenu: () => void;
  username: string;
  userInitials: string;
  go_profile: () => void;
  userRole?: string;
  isAdmin?: boolean;
  onReportProblem: () => void;
}

const Menu: React.FC<MenuProps> = ({
  mainMenuItems,
  menuOpen,
  handleLogout,
  toggleMenu,
  username,
  userInitials,
  go_profile,
  userRole,
  isAdmin,
  onReportProblem,
}) => {
  const { t } = useTranslation('Menu');
  const { openHelp } = useHelp();
  const { t: tReport } = useTranslation('ProblemReport');
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  const NavLink = ({ item }: { item: MenuItem }) => {
    const isActive = location.pathname.startsWith(item.link);
    return (
      <Link to={item.link} title={item.title}>
        <div
          className={`relative flex items-center gap-4 h-11 rounded-lg cursor-pointer transition-colors ${
            menuOpen ? 'px-4' : 'px-0 justify-center'
          } ${
            isActive
              ? 'bg-primary-dui/10 text-primary-dui font-semibold'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <i className={`${item.icon} text-xl`}></i>
          {menuOpen && <span className="text-sm whitespace-nowrap">{item.title}</span>}
          {isActive && (
            <div
              className={`absolute h-6 w-1 bg-primary-dui rounded-full transition-all ${
                menuOpen ? 'right-[-8px]' : 'right-[-4px]'
              }`}
            ></div>
          )}
        </div>
      </Link>
    );
  };

  return (
    <aside
      className={`fixed bottom-4 left-4 top-4 z-30 hidden flex-col overflow-visible rounded-2xl border border-gray-200 bg-card-color-100 p-3 shadow-sm transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-800 md:flex ${
        menuOpen ? 'w-60' : 'w-20'
      }`}
    >
      <div className="flex shrink-0 justify-center border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={toggleMenu}
            className="h-8 w-8 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            title={menuOpen ? t('Collapse menu') : t('Expand menu')}
          >
            <i className={`ri-menu-${menuOpen ? 'fold' : 'unfold'}-line text-xl`}></i>
          </button>
        </div>
      {/* Logo Section */}
      <div className="mb-4 flex h-16 shrink-0 items-center justify-center px-2 text-[#2B6CB0] dark:text-white">
        {menuOpen ? (
          <DeepechoWordmark
            className="h-5 w-auto max-w-full transition-all duration-300"
            title={t('logoAlt')}
          />
        ) : (
          <DeepechoMark
            className="h-7 w-auto transition-all duration-300"
            title={t('logoAlt')}
          />
        )}
      </div>

      {/* Facility selector (multi-facility users only) */}
      <FacilitySelector menuOpen={menuOpen} />

      {/* Main Navigation */}
      <nav className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden">
        {mainMenuItems.map((item, index) => (
          <NavLink key={index} item={item}/>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="shrink-0">
        {/* Profile Dropdown */}
        <div className="relative mb-2" ref={dropdownRef}>
          <NotificationInbox variant="sidebar" menuOpen={menuOpen} />
          <div
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            title={username}
            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${!menuOpen && 'justify-center'}`}
          >
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 font-bold text-sm flex-shrink-0">
              {userInitials}
            </div>
            {menuOpen && (
              <div className="flex-1 overflow-hidden flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                  {username}
                </p>
                <i className={`ri-arrow-${profileDropdownOpen ? 'up' : 'down'}-s-line text-sm text-gray-500 dark:text-gray-400`}></i>
              </div>
            )}
          </div>

          {/* Dropdown Menu */}
          {profileDropdownOpen && (
            <div className={`absolute bottom-full mb-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 ${
              menuOpen 
                ? 'left-0 right-0' 
                : 'left-0 w-48'
            }`}>
              <div
                onClick={() => {
                  go_profile();
                  setProfileDropdownOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <i className="ri-user-line text-base"></i>
                <span>{t('Go to Profile')}</span>
              </div>
              {/* {isAdmin && (
                <Link to="/organization">
                  <div
                    onClick={() => setProfileDropdownOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <i className="ri-building-line text-base"></i>
                    <span>{t('Organization Settings')}</span>
                  </div>
                </Link>
              )} */}
              <div
                onClick={() => {
                  handleLogout();
                  setProfileDropdownOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-base"></i>
                <span>{t('Logout')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Help */}
        <button
          type="button"
          onClick={() => openHelp()}
          title={t('Help')}
          aria-label={t('Help')}
          className={`relative flex w-full items-center gap-4 h-11 rounded-lg cursor-pointer transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ${
            menuOpen ? 'px-4' : 'px-0 justify-center'
          }`}
        >
          <i className="ri-question-line text-xl"></i>
          {menuOpen && <span className="text-sm whitespace-nowrap">{t('Help')}</span>}
        </button>

        <button
          type="button"
          onClick={onReportProblem}
          title={tReport('menuEntry')}
          className={`relative flex w-full items-center gap-4 h-11 rounded-lg cursor-pointer transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ${
            menuOpen ? 'px-4' : 'px-0 justify-center'
          }`}
        >
          <i className="ri-feedback-line text-xl"></i>
          {menuOpen && <span className="text-sm whitespace-nowrap">{tReport('menuEntry')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default Menu;